import { apiClient } from '../api'
import type { Organization, SubRequirementType, RecordType } from '@reqtrack/shared'

export interface ProductWithCounts {
  id: string
  orgId: string
  name: string
  prefix: string
  description: string | null
  createdAt: Date
  updatedAt: Date
  formattedId: string
  counts: {
    userRequirements: number
    productRequirements: number
    subRequirements: number
    records: number
    hazards: number
  }
}

export interface ProductWithTypes {
  id: string
  orgId: string
  name: string
  prefix: string
  description: string | null
  profile: string | null
  riskMethodology: string | null
  softwareOnly: boolean
  createdAt: Date
  updatedAt: Date
  formattedId: string
  subRequirementTypes: SubRequirementType[]
  recordTypes: RecordType[]
}

export interface SubRequirementTypeWithCount extends SubRequirementType {
  subRequirementCount: number
}

export interface RecordTypeWithCount extends RecordType {
  recordCount: number
}

export async function getProducts(orgId: string): Promise<{ data: ProductWithCounts[] }> {
  const res = await apiClient.get<{ data: ProductWithCounts[] }>(`/orgs/${orgId}/products`)
  return res.data
}

export async function getProduct(orgId: string, productId: string): Promise<{ data: ProductWithTypes }> {
  const res = await apiClient.get<{ data: ProductWithTypes }>(`/orgs/${orgId}/products/${productId}`)
  return res.data
}

export async function createProduct(
  orgId: string,
  data: { name: string; prefix: string; description?: string }
): Promise<{ data: Organization }> {
  const res = await apiClient.post<{ data: Organization }>(`/orgs/${orgId}/products`, data)
  return res.data
}

export async function updateProduct(
  orgId: string,
  productId: string,
  data: { name?: string; description?: string }
): Promise<{ data: Organization }> {
  const res = await apiClient.patch<{ data: Organization }>(`/orgs/${orgId}/products/${productId}`, data)
  return res.data
}

export async function getSubRequirementTypes(productId: string): Promise<{ data: SubRequirementTypeWithCount[] }> {
  const res = await apiClient.get<{ data: SubRequirementTypeWithCount[] }>(
    `/products/${productId}/sub-requirement-types`
  )
  return res.data
}

export async function createSubRequirementType(
  productId: string,
  data: { name: string; abbreviation: string }
): Promise<{ data: SubRequirementType }> {
  const res = await apiClient.post<{ data: SubRequirementType }>(
    `/products/${productId}/sub-requirement-types`,
    data
  )
  return res.data
}

export async function updateSubRequirementType(
  productId: string,
  typeId: string,
  data: { name?: string; abbreviation?: string }
): Promise<{ data: SubRequirementType }> {
  const res = await apiClient.patch<{ data: SubRequirementType }>(
    `/products/${productId}/sub-requirement-types/${typeId}`,
    data
  )
  return res.data
}

export async function deleteSubRequirementType(productId: string, typeId: string): Promise<void> {
  await apiClient.delete(`/products/${productId}/sub-requirement-types/${typeId}`)
}

export async function getRecordTypes(productId: string): Promise<{ data: RecordTypeWithCount[] }> {
  const res = await apiClient.get<{ data: RecordTypeWithCount[] }>(`/products/${productId}/record-types`)
  return res.data
}

export async function createRecordType(
  productId: string,
  data: { name: string; abbreviation: string }
): Promise<{ data: RecordType }> {
  const res = await apiClient.post<{ data: RecordType }>(`/products/${productId}/record-types`, data)
  return res.data
}

export async function updateRecordType(
  productId: string,
  typeId: string,
  data: { name?: string; abbreviation?: string }
): Promise<{ data: RecordType }> {
  const res = await apiClient.patch<{ data: RecordType }>(`/products/${productId}/record-types/${typeId}`, data)
  return res.data
}

export async function deleteRecordType(productId: string, typeId: string): Promise<void> {
  await apiClient.delete(`/products/${productId}/record-types/${typeId}`)
}
